/**
 * Who is signed in (FR-ACC-03). E-mail or username + password, then an authenticator
 * code where the role requires it. A first sign-in asks for a new password and, for
 * privileged roles, authenticator enrolment before anything else. Permissions and
 * assigned branches come from the session payload (auth/me shape).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, baseHeaders, setAccessToken, setUnauthenticatedHandler } from './api';
import { EP } from './endpoints';
import type { SessionResponse, SessionUser } from './types';

export type { SessionResponse, SessionUser } from './types';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  /** Why the last session ended, shown once on the sign-in screen. */
  endedReason: 'idle' | 'signed-out' | 'expired' | null;
  adopt: (r: SessionResponse) => void;
  reissue: () => Promise<SessionUser>;
  signOut: (reason?: 'idle' | 'signed-out') => Promise<void>;
  /** True when the user holds ANY of the given permissions. */
  can: (...permissions: string[]) => boolean;
}

const Ctx = createContext<AuthState | null>(null);

/** Partial payloads still produce a usable user (defensive against contract drift). */
export function normalizeUser(u: Partial<SessionUser> & { id: string; name: string }): SessionUser {
  return {
    username: '',
    email: null,
    roleCode: '',
    roleName: '',
    permissions: [],
    allBranches: false,
    branchIds: [],
    defaultBranchId: null,
    mfaEnabled: false,
    mfaRequired: false,
    mustChangePassword: false,
    ...u,
  } as SessionUser;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [endedReason, setEndedReason] = useState<AuthState['endedReason']>(null);
  const qc = useQueryClient();

  const clear = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    qc.clear();
  }, [qc]);

  const adopt = useCallback((r: SessionResponse) => {
    setAccessToken(r.accessToken);
    setUser(normalizeUser(r.user));
    setEndedReason(null);
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setEndedReason((prev) => prev ?? 'expired');
      clear();
    });
    void fetch(`/api/v1${EP.auth.refresh}`, { method: 'POST', credentials: 'include', headers: baseHeaders() })
      .then(async (res) => (res.ok ? adopt((await res.json()) as SessionResponse) : undefined))
      .catch(() => undefined)
      .finally(() => setLoading(false));
    return () => setUnauthenticatedHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reissue = useCallback(async () => {
    const r = await api<SessionResponse>(EP.auth.reissue, { method: 'POST', body: {} });
    adopt(r);
    return normalizeUser(r.user);
  }, [adopt]);

  const signOut = useCallback(
    async (reason: 'idle' | 'signed-out' = 'signed-out') => {
      await fetch(`/api/v1${EP.auth.logout}`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...baseHeaders(), 'content-type': 'application/json' },
        body: '{}',
      }).catch(() => undefined);
      setEndedReason(reason);
      clear();
    },
    [clear],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      endedReason,
      adopt,
      reissue,
      signOut,
      // Owner/Admin has everything (PRD §6). The API still checks every call.
      can: (...perms) => !!user && (perms.length === 0 || user.roleCode === 'OWNER_ADMIN' || perms.some((p) => user.permissions.includes(p))),
    }),
    [user, loading, endedReason, adopt, reissue, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
